import React, { useState, useEffect, useRef } from "react";
import axios from "axios";
import { BASE_URL } from "../config";
import { getAuthHeaders } from "../services/useAuth";
import * as XLSX from "xlsx";
import CustomButton from "../pages/CustomButton";
import { Upload, Download } from "lucide-react";
import Loader from "../dist/Loader";
import ExcelJS from "exceljs";
import { saveAs } from "file-saver";
const POBTargetCombined = () => {
  const [states, setStates] = useState([]);
  const [divisions, setDivisions] = useState([]);
  const [selectedState, setSelectedState] = useState("All");
  const [selectedDivision, setSelectedDivision] = useState("All");
  const [selectedQuarter, setSelectedQuarter] = useState("");
  const [quarterOptions, setQuarterOptions] = useState([]);
  const [reportData, setReportData] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [historyModalOpen, setHistoryModalOpen] = useState(false);
  const [historyEmployeeId, setHistoryEmployeeId] = useState(null);
  const [historyEmployeeName, setHistoryEmployeeName] = useState("");
  const [historyData, setHistoryData] = useState([]);
  const [editingRow, setEditingRow] = useState(null);
  const [editRowData, setEditRowData] = useState({
    target: "",
    start_date: "",
  });

  // Upload states
  const [uploadModalOpen, setUploadModalOpen] = useState(false);
  const [fromDate, setFromDate] = useState("");
  const [selectedFile, setSelectedFile] = useState(null);
  const [uploadStatus, setUploadStatus] = useState("");
  const [employees, setEmployees] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);

  const printRef = useRef();

  // Date formatting functions
  const normalizeDate = (dateStr) => {
    if (!dateStr) return "";
    const parts = dateStr.split("-");
    if (parts.length !== 3) return "";
    if (parts[0].length === 4) {
      return `${parts[2].padStart(2, "0")}-${parts[1].padStart(2, "0")}-${parts[0]
        }`;
    }
    return `${parts[0].padStart(2, "0")}-${parts[1].padStart(2, "0")}-${parts[2]
      }`;
  };

  const toYYYYMMDD = (ddmmyyyy) => {
    const [dd, mm, yyyy] = ddmmyyyy.split("-");
    return `${yyyy}-${mm}-${dd}`;
  };

  // --- NEW: quarter helpers ---
  // returns label like "Q3-2025" for the given date using your fiscal mapping
  const getFiscalQuarterLabelForDate = (date = new Date()) => {
    const month = date.getMonth(); // 0..11
    let q = "Q1";
    let fiscalYear = date.getFullYear();
    if (month >= 3 && month <= 5) {
      q = "Q1";
    } else if (month >= 6 && month <= 8) {
      q = "Q2";
    } else if (month >= 9 && month <= 11) {
      q = "Q3";
    } else {
      q = "Q4";
      fiscalYear = date.getFullYear() - 1; // Jan-Mar belong to previous fiscal label year
    }
    return `${q}-${fiscalYear}`;
  };

  // generate options for current fiscal year and next fiscal year, placing current quarter first
  const generateQuarterOptions = (forDate = new Date()) => {
    const month = forDate.getMonth();
    let fiscalBaseYear = forDate.getFullYear();
    if (month >= 0 && month <= 2) fiscalBaseYear = forDate.getFullYear() - 1;
    const fiscalYears = [fiscalBaseYear, fiscalBaseYear + 1];
    const quarters = [];
    fiscalYears.forEach((fy) => {
      ["Q1", "Q2", "Q3", "Q4"].forEach((q) => quarters.push(`${q}-${fy}`));
    });
    const current = getFiscalQuarterLabelForDate(forDate);
    return [current, ...quarters.filter((x) => x !== current)];
  };

  const formatQuarterDisplay = (quarter) => {
    if (!quarter) return "";
    const [q, year] = quarter.split("-");
    const fiscalYear = parseInt(year);

    const map = {
      Q1: { months: "Apr - Jun", displayYear: fiscalYear },
      Q2: { months: "Jul - Sep", displayYear: fiscalYear },
      Q3: { months: "Oct - Dec", displayYear: fiscalYear },
      Q4: { months: "Jan - Mar", displayYear: fiscalYear + 1 },
    };

    const info = map[q];
    return `${q} (${info.months}) ${info.displayYear}`;
  };
  // --- END new helpers ---

  useEffect(() => {
    const fetchStates = async () => {
      try {
        const response = await axios.get(`${BASE_URL}/api/states`, {
          headers: getAuthHeaders(),
        });
        setStates(response.data);
      } catch (err) {
        console.error("Error fetching states:", err);
        setError("Failed to load states");
      }
    };
    fetchStates();
  }, []);

  useEffect(() => {
    const fetchDivisions = async () => {
      if (selectedState && selectedState !== "All") {
        try {
          const response = await axios.get(
            `${BASE_URL}/api/divisions?state_id=${selectedState}`,
            { headers: getAuthHeaders() },
          );
          setDivisions(response.data);
          setError(null);
        } catch (err) {
          console.error("Error fetching divisions:", err);
          setError("Failed to load divisions");
        }
      } else {
        setDivisions([]);
        setSelectedDivision("All");
      }
    };
    fetchDivisions();
  }, [selectedState]);

  // Build quarter options on mount and default select current quarter
  useEffect(() => {
    const opts = generateQuarterOptions(new Date());
    setQuarterOptions(opts);
    if (!selectedQuarter) setSelectedQuarter(opts[0]);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Load employees on component mount
  useEffect(() => {
    const fetchEmployees = async () => {
      try {
        const { data } = await axios.get(
          `${BASE_URL}/api/pob-targets/employee`,
          {
            params: { skip: 0, limit: 1000 },
            headers: getAuthHeaders(),
          },
        );
        setEmployees(data);
      } catch (err) {
        setError("Failed to fetch employee data. Please try again.");
        console.error("API Error:", err.response?.data || err.message);
      }
    };
    fetchEmployees();
  }, []);

  const handleFileUpload = async () => {
    setError("");
    setUploadStatus("");

    if (!selectedFile || !fromDate) {
      setError("Please select both a valid date and a file.");
      return;
    }

    const formattedDate = toYYYYMMDD(normalizeDate(fromDate));

    if (!formattedDate || formattedDate === "--") {
      setError("Invalid date format.");
      return;
    }

    const formData = new FormData();
    formData.append("file", selectedFile);
    formData.append("from_date", formattedDate);

    try {
      setUploadStatus("Uploading...");

      const response = await axios.post(
        `${BASE_URL}/api/pob-targets/employee-targets/upload-bulk`,
        formData,
        {
          headers: {
            ...getAuthHeaders(),
            "Content-Type": "multipart/form-data",
          },
        },
      );

      setUploadStatus(response.data.message || "Upload successful.");

      const { data } = await axios.get(`${BASE_URL}/api/pob-targets/employee`, {
        params: { skip: 0, limit: 1000 },
        headers: getAuthHeaders(),
      });
      setEmployees(data);
      setCurrentPage(1);

      setSelectedFile(null);
      document.getElementById("file-input").value = "";

      setTimeout(() => {
        setUploadModalOpen(false);
        setEmployees([]);
      }, 1500);
    } catch (err) {
      const resData = err.response?.data;
      let message = "Upload failed.";

      if (typeof resData?.detail === "string") {
        message = resData.detail;
      } else if (Array.isArray(resData?.detail)) {
        message = resData.detail.map((e) => e.msg).join(", ");
      } else if (typeof resData?.detail === "object") {
        message = JSON.stringify(resData.detail);
      }

      setError(message || "Upload failed. Please try again.");
      setUploadStatus("");
    }
  };

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setSelectedFile(file);
      setError("");
      setUploadStatus("");
    }
  };

  // Report functions
  const getQuarterDateRange = (quarter) => {
    const [q, year] = quarter.split("-");
    let startDate, endDate;
    switch (q) {
      case "Q1":
        startDate = `01/Apr/${year}`;
        endDate = `30/Jun/${year}`;
        break;
      case "Q2":
        startDate = `01/Jul/${year}`;
        endDate = `30/Sep/${year}`;
        break;
      case "Q3":
        startDate = `01/Oct/${year}`;
        endDate = `31/Dec/${year}`;
        break;
      case "Q4":
        startDate = `01/Jan/${parseInt(year) + 1}`;
        endDate = `31/Mar/${parseInt(year) + 1}`;
        break;
      default:
        startDate = `01/Apr/${year}`;
        endDate = `30/Jun/${year}`;
    }
    return `${startDate}-${endDate}`;
  };

  const generateReport = async () => {
    setIsLoading(true);
    setError(null);
    try {
      const quarterDateRange = getQuarterDateRange(selectedQuarter);
      const params = {
        quarter: quarterDateRange,
      };

      if (selectedState && selectedState !== "All") {
        params.state_id = selectedState;
      }

      if (selectedDivision && selectedDivision !== "All") {
        const division = divisions.find((d) => d.id == selectedDivision);
        params.division = division?.name;
      }

      const response = await axios.get(
        `${BASE_URL}/api/pob-targets/employee-targets/merged-report?`,
        {
          params,
          headers: getAuthHeaders(),
        },
      );
      setReportData(response.data.data || []);
    } catch (err) {
      console.error("Error fetching report:", err);
      setError("Failed to generate report");
    } finally {
      setIsLoading(false);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return "-";
    const date = new Date(dateString);
    return date.toLocaleDateString("en-GB", {
      day: "2-digit",
      month: "short",
      year: "numeric",
    });
  };

  const formatCurrency = (value) => {
    return value
      ? parseFloat(value).toLocaleString("en-IN", {
        maximumFractionDigits: 2,
        minimumFractionDigits: 2,
      })
      : "0.00";
  };

  const handlePrint = () => {
    const printWindow = window.open("", "_blank");

    if (!printWindow) {
      alert("Pop-up blocked. Please allow pop-ups to print.");
      return;
    }

    const tableHtml =
      printRef.current?.innerHTML || "<div>No table data</div>";

    const quarterLabel = formatQuarterDisplay(selectedQuarter);

    const stateName =
      states.find((s) => s.id == selectedState)?.name || "All";

    const divisionName =
      divisions.find((d) => d.id == selectedDivision)?.name || "All";

    printWindow.document.write(`
    <html>
      <head>
        <title>POB vs SOB Target Report</title>
        <style>
          @page {
            size: landscape;
            margin: 10mm;
          }

          body {
            font-family: Arial, sans-serif;
            padding: 16px;
          }

          .print-header {
            margin-bottom: 12px;
          }

          .print-title {
            font-size: 18px;
            font-weight: bold;
            text-align: center;
            margin-bottom: 6px;
          }

          .print-meta {
            font-size: 13px;
            display: flex;
            justify-content: space-between;
            margin-bottom: 10px;
          }

          table {
            width: 100%;
            border-collapse: collapse;
            font-size: 11px;
          }

          th, td {
            border: 1px solid #000;
            padding: 4px;
            white-space: nowrap;
          }

          th {
            background: #f2f2f2;
            font-weight: bold;
          }

          thead {
            display: table-header-group;
          }

          tr {
            page-break-inside: avoid;
          }
        </style>
      </head>

      <body>
        <div class="print-header">
          <div class="print-title">POB vs SOB Target Report</div>
          <div class="print-meta">
            <div><b>State:</b> ${stateName}</div>
            <div><b>Division:</b> ${divisionName}</div>
            <div><b>Quarter:</b> ${quarterLabel}</div>
          </div>
          <div style="font-size:12px;">
            Generated on: ${new Date().toLocaleString()}
          </div>
        </div>

        ${tableHtml}

        <script>
          window.onload = () => setTimeout(() => window.print(), 300);
        </script>
      </body>
    </html>
  `);

    printWindow.document.close();
  };


 const handleExportExcel = async () => {
  if (!reportData.length) return;

  const workbook = new ExcelJS.Workbook();
  const sheet = workbook.addWorksheet("POB_SOB_Target_Report");

  // Columns
  sheet.columns = [
    { header: "S.No", key: "sno", width: 8 },
    { header: "Emp ID", key: "empId", width: 15 },
    { header: "Employee", key: "employee", width: 30 },
    { header: "Division", key: "division", width: 20 },
    { header: "State", key: "state", width: 18 },
    { header: "HQ", key: "hq", width: 20 },
    { header: "Designation", key: "designation", width: 25 },
    { header: "DOJ", key: "doj", width: 15 },
    { header: "DOR", key: "dor", width: 15 },
    { header: "Phone", key: "phone", width: 18 },
    { header: "POB Target", key: "pob", width: 18 },
    { header: "SOB Target", key: "sob", width: 18 },
    { header: "From Date", key: "from", width: 15 },
    { header: "End Date", key: "end", width: 20 },
  ];

  // Add Rows
 reportData.forEach((item, index) => {
    // ✅ EMP ID fix — same as VisitSupport
    // Pure digits (5960) → Number → no warning, VLOOKUP works
    // Alpha-num (C2341)  → String → no warning by nature, VLOOKUP works
    const empRaw   = String(item.employee ?? "").trim();
    const empIsNum = /^\d+$/.test(empRaw);
    const empID    = empIsNum ? Number(empRaw) : empRaw;

    const row = sheet.addRow({
      sno: index + 1,
      empId: empID,
      employee: item.employee_name,
      division: item.division,
      state: item.state,
      hq: item.hq,
      designation: item.designation,
      doj: item.final_joining_date,
      dor: item.resign_date,
      phone: item.phone,
      pob: Math.round(Number(item.pob_target || 0)),
      sob: Math.round(Number(item.sob_target || 0)),
      from: item.start_date,
      end: item.end_date || "Till Next Change",
    });

    // ✅ Apply correct numFmt to EMP ID cell after row is added
    const empCell    = row.getCell("empId");
    empCell.numFmt   = empIsNum ? "0" : "@";
  });
  // =============================
  // HEADER STYLE
  // =============================
  sheet.getRow(1).eachCell((cell) => {
    cell.font = { bold: true, color: { argb: "FFFFFFFF" } };
    cell.fill = {
      type: "pattern",
      pattern: "solid",
      fgColor: { argb: "4472C4" }, // Blue header
    };
    cell.alignment = { horizontal: "center", vertical: "middle" };
  });

  // =============================
  // BORDER STYLE (LIKE YOUR IMAGE)
  // =============================
  sheet.eachRow((row, rowNumber) => {
    row.eachCell((cell) => {
      cell.border = {
        top: { style: "thin" },
        left: { style: "thin" },
        bottom: { style: "thin" },
        right: { style: "thin" },
      };

      cell.alignment = {
        vertical: "middle",
        horizontal: rowNumber === 1 ? "center" : "left",
      };
    });
  });

  // =============================
  // NUMBER FORMAT
  // =============================
  ["K", "L"].forEach((col) => {
    sheet.getColumn(col).numFmt = "0";
  });

  // =============================
  // FREEZE HEADER
  // =============================
  sheet.views = [{ state: "frozen", ySplit: 1 }];

  // =============================
  // AUTO FILTER
  // =============================
  sheet.autoFilter = {
    from: "A1",
    to: "N1",
  };

  // =============================
  // DOWNLOAD
  // =============================
  const buffer = await workbook.xlsx.writeBuffer();
  saveAs(new Blob([buffer]), "POB_SOB_Target_Report.xlsx");
};

const handleDownloadTemplate = async () => {
  const workbook = new ExcelJS.Workbook();
  const sheet = workbook.addWorksheet("Template");

  sheet.columns = [
    { header: "EMPID", key: "empId", width: 12 },
    { header: "EMPLOYEE", key: "employee", width: 25 },
    { header: "POB_TARGET", key: "pobTarget", width: 15 },
    { header: "SOB_TARGET", key: "sobTarget", width: 15 },
    { header: "FROM_DATE", key: "fromDate", width: 15 },
  ];

  // Sample row
  sheet.addRow({
    empId: 7,
    employee: "Nareshreddy Malreddy",
    pobTarget: 1300000,
    sobTarget: 1300000,
    fromDate: "01-Nov-21",
  });

  sheet.getRow(1).eachCell((cell) => {
    cell.font = { bold: true, color: { argb: "FFFFFFFF" } };
    cell.fill = {
      type: "pattern",
      pattern: "solid",
      fgColor: { argb: "4472C4" },
    };
    cell.alignment = { horizontal: "center", vertical: "middle" };
    cell.border = {
      top: { style: "thin" },
      left: { style: "thin" },
      bottom: { style: "thin" },
      right: { style: "thin" },
    };
  });

  sheet.eachRow((row, rowNumber) => {
    row.eachCell((cell) => {
      cell.border = {
        top: { style: "thin" },
        left: { style: "thin" },
        bottom: { style: "thin" },
        right: { style: "thin" },
      };
    });
  });

  const buffer = await workbook.xlsx.writeBuffer();
  saveAs(new Blob([buffer]), "POB_Target_Upload_Template.xlsx");
};
  const openHistoryModal = async (employee) => {
    try {
      setHistoryEmployeeId(employee.employee_id);
      setHistoryEmployeeName(employee.employee_name);
      const response = await axios.get(
        `${BASE_URL}/api/pob-targets/employee-history/${employee.employee_id}`,
        { headers: getAuthHeaders() },
      );
      setHistoryData(response.data);
      setHistoryModalOpen(true);
    } catch (err) {
      console.error("Error fetching POB history:", err);
      alert("Failed to load POB history");
    }
  };

  const closeHistoryModal = async () => {
    setHistoryModalOpen(false);
    setHistoryEmployeeId(null);
    setHistoryEmployeeName("");
    setHistoryData([]);
    if (selectedState && selectedDivision) {
      await generateReport();
    }
  };

  const handleEditRow = (row) => {
    setEditingRow(row.employee_id);

    setEditRowData({
      start_date: row.start_date ? row.start_date.split("T")[0] : "",
      pob_target: row.pob_target ?? "",
      sob_target: row.sob_target ?? "",
    });
  };

  const handleSaveRow = async (item) => {
    try {
      if (!editRowData.start_date) {
        alert("Please select start date");
        return;
      }

      if (
        (editRowData.pob_target === "" || editRowData.pob_target == null) &&
        (editRowData.sob_target === "" || editRowData.sob_target == null)
      ) {
        alert("Please enter at least one target (POB / SOB)");
        return;
      }

      const isUpdate = Boolean(item.pob_id || item.sob_id);

      const payload = {
        employee_id: item.employee_id,
        start_date: editRowData.start_date,

        sob: {
          target: Number(editRowData.sob_target) || 0,
        },

        pob: {
          target: Number(editRowData.pob_target) || 0,
        },
      };

      if (isUpdate) {
        if (item.sob_id) payload.sob.target_id = item.sob_id;
        if (item.pob_id) payload.pob.target_id = item.pob_id;
      }

      if (isUpdate) {
        await axios.put(
          `${BASE_URL}/api/pob-targets/employee-targets/bulk`,
          payload,
          { headers: getAuthHeaders() },
        );
        alert("Targets updated successfully!");
      } else {
        await axios.post(
          `${BASE_URL}/api/pob-targets/employee-targets/bulk`,
          payload,
          { headers: getAuthHeaders() },
        );
        alert("Targets added successfully!");
      }

      await generateReport();
      setEditingRow(null);
    } catch (err) {
      console.error("Error saving targets:", err);
      alert(err.response?.data?.detail || "Failed to save targets");
    }
  };

  const handleDeleteRow = async (item) => {
    if (!item.pob_id && !item.sob_id) {
      alert("No target to delete for this employee.");
      return;
    }

    if (!window.confirm("Are you sure you want to delete this target?")) return;

    try {
      await axios.delete(`${BASE_URL}/api/pob-targets/employee-targets/bulk`, {
        data: {
          employee_id: item.employee_id,
          pob_id: item.pob_id ?? null,
          sob_id: item.sob_id ?? null,
        },
        headers: getAuthHeaders(),
      });

      await generateReport();
      alert("POB & SOB targets deleted successfully!");
    } catch (err) {
      console.error("Error deleting targets:", err);
      alert(err.response?.data?.detail || "Failed to delete targets");
    }
  };

  const handleEditRowChange = (e) => {
    const { name, value } = e.target;
    setEditRowData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const openUploadModal = () => {
    setUploadModalOpen(true);
    setFromDate("");
    setSelectedFile(null);
    setUploadStatus("");
    setEmployees([]);
    setError("");
  };

  const closeUploadModal = () => {
    setUploadModalOpen(false);
    setFromDate("");
    setSelectedFile(null);
    setUploadStatus("");
    setEmployees([]);
    setError("");
  };

  return (
    <div className="p-2  border-2 border-gray-300 rounded-lg bg-white">
      <main className="flex-1">
        {/* Report Section */}
        <div className="p-2 border-2 border-gray-300 rounded-lg shadow-sm">
          <div className="flex justify-between items-center mb-4">
            <div className="flex justify-center w-full">
              <h1
                className="text-xl sm:text-2xl font-bold text-center 
                        bg-gradient-to-r from-blue-700 via-blue-500 to-blue-300 
                        bg-clip-text text-transparent"
              >
                POB VS SOB Target Report
              </h1>
            </div>
           <div className="flex items-center gap-3">
              {/* Template Download Button */}
              <button
                onClick={handleDownloadTemplate}
                title="Download Excel Template"
                className="flex items-center gap-2 bg-green-600 text-white font-medium px-4 py-2 rounded-lg 
                   hover:bg-green-700 active:scale-[0.98] transition-all duration-200 shadow-md text-nowrap"
              >
                <Download className="w-5 h-5" />
                <span>Template</span>
              </button>

              {/* Upload Button */}
              <button
                onClick={openUploadModal}
                className="flex items-center gap-2 bg-blue-600 text-white font-medium px-4 py-2 rounded-lg 
                   hover:bg-indigo-700 active:scale-[0.98] transition-all duration-200 shadow-md text-nowrap"
              >
                <Upload className="w-5 h-5" />
                <span>Upload Targets</span>
              </button>
            </div>
          </div>
          <div className="flex flex-wrap gap-5 items-center justify-center">
            <div>
              <label>State</label>
              <br />
              <select
                value={selectedState}
                onChange={(e) => setSelectedState(e.target.value)}
                className="w-60 p-1.5 text-sm border border-gray-300"
              >
                <option value="All">All</option>
                {states.map((s) => (
                  <option key={s.id} value={s.id}>
                    {s.name}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label>Division</label>
              <br />
              <select
                value={selectedDivision}
                onChange={(e) => setSelectedDivision(e.target.value)}
                className="w-60 p-1.5 text-sm border border-gray-300"
                disabled={!selectedState || selectedState === "All"}
              >
                <option value="All">All Division</option>
                {divisions.map((d) => (
                  <option key={d.id} value={d.id}>
                    {d.name}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label>Quarter</label>
              <br />
              <select
                value={selectedQuarter}
                onChange={(e) => setSelectedQuarter(e.target.value)}
                className="w-60 p-1.5 text-sm border border-gray-300"
              >
                {quarterOptions.map((q) => (
                  <option key={q} value={q}>
                    {formatQuarterDisplay(q)}
                  </option>
                ))}
              </select>
            </div>
            <div className="mt-5">
              {isLoading ? (
                <div className="flex justify-center items-center py-4">
                  <Loader onCancel={() => setIsLoading(false)} />
                </div>
              ) : (
                <CustomButton
                  onClick={generateReport}
                  label="Go"
                  variant="go"
                />
              )}
            </div>
          </div>
        </div>

        {error && <div className="text-red-600 font-medium mb-4">{error}</div>}
        {isLoading && <div className="text-center mb-4">Loading...</div>}

        {/* Report Data Table */}
        {reportData.length > 0 && (
          <div className="p-2  border-2 border-gray-300 rounded-lg bg-white mt-5">
            <div ref={printRef}>
              <div className="border rounded shadow  table-wrapper">
                <table className="unlock-dcr-leave-table text-sm ">
                  <thead className="bg-blue-500 text-white">
                    <tr>
                      {[
                        "S.No",
                        "Empid",
                        "Employee",
                        "Division",
                        "State",
                        "HQ",
                        "Designation",
                        "DOJ",
                        "DOR",
                        "Phone",
                        "POB Target",
                        "SOB Target",
                        // "Running Total",
                        "From Date",
                        "End Date",
                        "Actions",
                        "History",
                      ].map((h, i) => (
                        <th key={i} className="border border-black p-1.5">
                          {h}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {reportData.map((item, i) => (
                      <tr key={item.employee_id}>
                        <td className="border border-black p-1.5">{i + 1}</td>
                        <td className="border border-black p-1.5">
                          {item.employee}
                        </td>
                        <td className="border border-black p-1.5">
                          {item.employee_name}
                        </td>
                        <td className="border border-black p-1.5">
                          {item.division}
                        </td>
                        <td className="border border-black p-1.5">
                          {item.state}
                        </td>
                        <td className="border border-black p-1.5">{item.hq}</td>
                        <td className="border border-black p-1.5">
                          {item.designation}
                        </td>
                        <td className="border border-black p-1.5">
                          {formatDate(item.final_joining_date)}
                        </td>
                        <td className="border border-black p-1.5">
                          {formatDate(item.resign_date)}
                        </td>
                        <td className="border border-black p-1.5">
                          {item.phone || "-"}
                        </td>
                        <td className="border border-black p-1.5 text-right">
                          {editingRow === item.employee_id ? (
                            <input
                              type="number"
                              name="pob_target" // ✅ FIXED
                              value={editRowData.pob_target ?? ""}
                              onChange={handleEditRowChange}
                              className="w-full p-1 border"
                              step="0.01"
                              min="0"
                            />
                          ) : (
                            formatCurrency(item.pob_target ?? 0)
                          )}
                        </td>

                        <td className="border border-black p-1.5 text-right">
                          {editingRow === item.employee_id ? (
                            <input
                              type="number"
                              name="sob_target"
                              value={editRowData.sob_target ?? ""}
                              onChange={handleEditRowChange}
                              className="w-full p-1 border"
                              step="0.01"
                              min="0"
                            />
                          ) : (
                            formatCurrency(item.sob_target ?? 0)
                          )}
                        </td>

                        {/* <td className="border border-black p-1.5 text-right">
                        {formatCurrency(item.running_total)}
                      </td> */}
                        <td className="border border-black p-1.5">
                          {editingRow === item.employee_id ? (
                            <input
                              type="date"
                              name="start_date"
                              value={editRowData.start_date}
                              onChange={handleEditRowChange}
                              className="w-full p-1 border"
                            />
                          ) : (
                            formatDate(item.start_date)
                          )}
                        </td>
                        <td className="border border-black p-1.5">
                          {formatDate(item.end_date)}
                        </td>
                        <td className="border border-black p-1.5 text-center">
                          {editingRow === item.employee_id ? (
                            <div className="flex justify-center gap-2">
                              <CustomButton
                                onClick={() => handleSaveRow(item)}
                                label={item.sob_id ? "Save" : "Add"}
                                variant={item.sob_id ? "go" : "add"}
                              />
                              <CustomButton
                                onClick={() => setEditingRow(null)}
                                label="Cancel"
                                variant="cancel"
                              />
                            </div>
                          ) : (
                            <>
                              {item.pob_id ? (
                                <>
                                  <CustomButton
                                    onClick={() => handleEditRow(item)}
                                    label="Edit"
                                    variant="edit"
                                  />
                                  <CustomButton
                                    onClick={() => handleDeleteRow(item)}
                                    label="Delete"
                                    variant="delete"
                                  />
                                </>
                              ) : (
                                <CustomButton
                                  onClick={() => handleEditRow(item)}
                                  label="Add Target"
                                  variant="add"
                                />
                              )}
                            </>
                          )}
                        </td>
                        <td className="border border-black p-1.5 text-center">
                          <CustomButton
                            onClick={() => openHistoryModal(item)}
                            label="History"
                            variant="history"
                          />
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                <div className="sob-button-container mt-4 flex gap-4 justify-center">
                  <CustomButton
                    onClick={handlePrint}
                    label="Print"
                    variant="print"
                  >
                    🖨
                  </CustomButton>
                  <CustomButton
                    onClick={handleExportExcel}
                    label="Export"
                    variant="export"
                  >
                    📥
                  </CustomButton>
                </div>
              </div>
            </div>
          </div>
        )}
      </main>

      {/* Upload Modal */}
      {uploadModalOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-lg w-11/12 max-w-4xl p-4 overflow-y-auto max-h-screen">
            <div className="flex justify-between items-center mb-4">
              <h2 className="text-xl font-bold">Upload POB Targets</h2>
              <button
                onClick={closeUploadModal}
                className="text-gray-600 hover:text-gray-800 text-lg font-bold"
              >
                &times;
              </button>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    From Date
                  </label>
                  <input
                    type="date"
                    value={fromDate}
                    onChange={(e) => setFromDate(e.target.value)}
                    className="w-full p-2 border border-gray-300 rounded"
                    required
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    File (CSV/Excel)
                  </label>
                  <input
                    id="file-input"
                    type="file"
                    accept=".csv,.xlsx,.xls"
                    onChange={handleFileChange}
                    className="w-full p-2 border border-gray-300 rounded"
                    required
                  />
                </div>
                <div className="pt-2">
                  <CustomButton
                    onClick={handleFileUpload}
                    label="Upload"
                    variant="go"
                    fullWidth
                  />
                </div>

                {uploadStatus && (
                  <div
                    className={`mt-2 p-2 rounded text-center ${uploadStatus.includes("success")
                      ? "bg-green-100 text-green-700"
                      : "bg-blue-100 text-blue-700"
                      }`}
                  >
                    {uploadStatus}
                  </div>
                )}
                {error && (
                  <div className="mt-2 p-2 rounded bg-red-100 text-red-700 text-center">
                    {error}
                  </div>
                )}
              </div>

              <div>
                <h3 className="text-lg font-medium mb-2">File Requirements</h3>
                <ul className="list-disc pl-5 space-y-1 text-sm text-gray-600">
                  <li>CSV or Excel file format</li>
                  <li>
                    Must contain columns: Employee ID, Employee Name, Target
                  </li>
                  <li>
                    Optional columns: Division, State, HQ, Designation, Phone
                  </li>
                  <li>Date format: DD-MM-YYYY or YYYY-MM-DD</li>
                  <li>Target values should be numeric</li>
                </ul>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* History Modal */}
      {historyModalOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-lg w-11/12 max-w-3xl p-4 overflow-y-auto max-h-screen">
            <div className="flex justify-between items-center mb-4">
              <h2 className="text-xl font-bold">
                Target History - {historyEmployeeName}
              </h2>
              <button
                onClick={closeHistoryModal}
                className="text-gray-600 hover:text-gray-800 text-lg font-bold"
              >
                &times;
              </button>
            </div>
            <table className="unlock-dcr-leave-table text-sm ">
              <thead className="bg-blue-500 text-white">
                <tr>
                  <th className="border border-black p-1.5">S.No</th>
                  <th className="border border-black p-1.5">POB Target</th>
                  <th className="border border-black p-1.5">SOB Target</th>
                  <th className="border border-black p-1.5">Start</th>
                  <th className="border border-black p-1.5">End</th>
                </tr>
              </thead>
              <tbody>
                {historyData.map((row, index) => (
                  <tr key={row.id}>
                    <td className="border border-black p-1.5 text-center">
                      {index + 1}
                    </td>
                    <td className="border border-black p-1.5">
                      {parseFloat(row.pob_target).toFixed(2)}
                    </td>
                    <td className="border border-black p-1.5">
                      {parseFloat(row.sob_target).toFixed(2)}
                    </td>
                    <td className="border border-black p-1.5">
                      {new Date(row.start_date).toLocaleDateString()}
                    </td>
                    <td className="border border-black p-1.5">
                      {row.end_date
                        ? new Date(row.end_date).toLocaleDateString()
                        : "Till Next Change"}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
};

export default POBTargetCombined;
